import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Wallet, Send, ReceiptText, LogOut, ChevronDown, User, FileText, HandCoins, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CustomerThemeContext } from '../context/CustomerThemeContext';
import Logo from './Logo';
import { LanguageSelect } from './LanguageSelect';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import '../styles/customer-wallet.css';
import { useCustomerCurrency } from '../hooks/useCustomerCurrency';
import { uiText } from '../lib/i18n';
import { PaymentNotifications } from './PaymentNotifications';

const NAV = [
  { to: '/portal', label: 'My Account', short: 'Home', icon: Wallet },
  { to: '/portal/send', label: 'Send Money', short: 'Send', icon: Send },
  { to: '/portal/history', label: 'Transactions', short: 'Activity', icon: ReceiptText },
  { to: '/portal/statement', label: 'Statement', short: 'Statement', icon: FileText },
  { to: '/portal/profile', label: 'Profile', short: 'Profile', icon: User },
];

export default function CustomerLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [currency] = useCustomerCurrency();
  const currencyQuery = `?currency=${currency}`;
  return <CustomerThemeContext.Provider value={true}>
    <div className="customer-app customer-themed" data-testid="customer-wallet-shell">
      <header className="customer-header print:hidden">
        <div className="customer-header-inner">
          <Link to={`/portal${currencyQuery}`} data-testid="customer-brand-home" aria-label="MALDHOWR home" className="customer-brand"><Logo dark showText={false} /></Link>
          <nav className="customer-desktop-nav" aria-label="Customer navigation" data-testid="customer-desktop-navigation">
            {NAV.map(({ to, label, icon: Icon }) => <NavLink key={to} to={`${to}${currencyQuery}`} end data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`} className={({ isActive }) => `customer-nav-link ${isActive ? 'is-active' : ''}`}><Icon size={17} /><span>{label}</span></NavLink>)}
          </nav>
          <div className="flex items-center gap-2 min-w-0">
            <PaymentNotifications />
            <div className="hidden sm:block"><LanguageSelect id="customer-language" compact /></div>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger data-testid="customer-user-menu" aria-label="Account menu" className="customer-user-trigger">
                <span className="customer-avatar">{user?.name?.[0]?.toUpperCase()}</span>
                <span className="hidden sm:block text-left min-w-0"><span data-testid="customer-header-name" className="block text-sm font-semibold truncate max-w-32">{user?.name}</span><span data-testid="customer-header-account-number" className="block text-xs customer-muted">{user?.accountNo}</span></span>
                <ChevronDown size={14} className="customer-muted" />
              </DropdownMenuTrigger>
              <DropdownMenuContent data-testid="customer-user-menu-content" align="end" className="w-60">
                <DropdownMenuLabel data-testid="customer-user-menu-shop" className="break-words">{user?.shopName || 'Customer portal'}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link data-testid="customer-menu-request" to={`/portal/request${currencyQuery}`}><HandCoins size={16} />Request Money</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link data-testid="customer-menu-requests" to={`/portal/requests${currencyQuery}`}><History size={16} />My Requests</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link data-testid="customer-menu-profile" to={`/portal/profile${currencyQuery}`}><User size={16} />Profile</Link></DropdownMenuItem>
                <div className="sm:hidden px-2 py-3" data-testid="customer-mobile-language-container"><LanguageSelect id="customer-mobile-language" onChanged={() => setMenuOpen(false)} /></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="customer-logout" onClick={() => { logout(); navigate('/login'); }} className="text-red-400 focus:text-red-300"><LogOut size={16} />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="customer-main" data-testid="customer-main-content">{children}</main>
      <nav className="customer-bottom-nav print:hidden" aria-label="Customer navigation" data-testid="customer-mobile-navigation">
        {NAV.map(({ to, label, short, icon: Icon }) => <NavLink key={to} to={`${to}${currencyQuery}`} end aria-label={label} data-testid={`customer-mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`} className={({ isActive }) => `customer-bottom-link ${isActive ? 'is-active' : ''}`}><Icon size={21} /><span>{uiText(short)}</span></NavLink>)}
      </nav>
    </div>
  </CustomerThemeContext.Provider>;
}